import React, { useState, useEffect } from 'react';
import { getMonsterImgPath } from '../../utils/monster';
import CounterPost from './CounterPost';
import TacticPost from './TacticPost';
import ProfileWithName from '../../components/ProfileWithName';
import DefenseTrend from '../../components/DefenseTrend';
import Caret from '../../components/Caret';
import Segmented from '../../components/Segmented';
import BackButton from '../../components/BackButton';
import SiegeRateTrend from '../../components/SiegeRateTrend';
import { minForPeriod } from '../../utils/period';
import { useBackHandler } from '../../hooks/useBackHandler';
import { guildImg } from '../../utils/guildImage';

interface PopularDefenseProps {
  monsterMapping: Record<string, any>;
  user: any;
  /** 통계 탭에서 카드를 눌러 들어왔을 때 — 머리 판 왼쪽 위에 [← 뒤로가기] 를 놓는다 */
  onBack?: () => void;
}

interface DefenseData {
  defenseTeam: any[];
  totalMatches: number;
  wins: number;
  losses: number;
  winRate: number;
}

const PopularDefense: React.FC<PopularDefenseProps> = ({
  monsterMapping,
  user,
  onBack
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedDefense, setSelectedDefense] = useState<DefenseData | null>(null);
  const [counterList, setCounterList] = useState<any>(null);
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);

  /**
   * 카운터 클릭 시 상세를 연다.
   * 점령전 기록은 게시물이 아니라 /api/posts/posting 으로 조회할 대상이 없다.
   * 404 본문을 그대로 넘기면 attackTeam 이 없는 객체가 되어 CounterPost 가 터진다.
   * 그런 경우 목록이 이미 갖고 있는 데이터를 그대로 쓴다.
   */
  const openCounter = async (c: any) => {
    const isSiegeRecord = typeof c?._id === 'string' && c._id.startsWith('siege-');
    if (!c?._id || isSiegeRecord) {
      setSelectedPost({
        ...c,
        attackTeam: c.attackTeam || c.monsters || [],
        defenseTeam: selectedDefense?.defenseTeam || [],
        nickname: c.author,
        profileImg: c.profileImg,
        source: c.source
      });
      return;
    }
    try {
      const res = await fetch(`/api/posts/posting/${c._id}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setSelectedPost({ ...data, nickname: c.author, profileImg: c.profileImg, source: c.source });
    } catch {}
  };

  const [selectedGroup, setSelectedGroup] = useState<{ key: string; items: any[] } | null>(null);
  const [groupTab, setGroupTab] = useState<'members' | 'records'>('members');
  const [expandedMemberKey, setExpandedMemberKey] = useState<string | null>(null);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  /**
   * 기간 — 방성률 그래프 밑 스크롤바로 고른 첫 판부터 지금까지 (사용자 2026-10-27 "방덱 통계도 다른 곳처럼 그래프 및 스크롤").
   * since = 첫 판 시작일(서버로), recent = 판 수('몇 회 이상' 문턱을 줄이는 데만)
   */
  const [since, setSince] = useState<string | null>(null);
  const [recent, setRecent] = useState(0);
  const minCount = minForPeriod(10, recent);
  const [starFilter, setStarFilter] = useState<number>(5); // 5성방덱 기본
  /**
   * 어느 쪽 방덱 통계인지.
   *   opp — 상대 길드 방덱 (공격 기록에서 뽑는다)
   *   own — 우리 길드 방덱 (방어 기록에서 뽑는다)
   * 승패는 두 탭 모두 공격하는 쪽 기준이라 방성률을 같은 식으로 읽는다.
   */
  const [defenseSide, setDefenseSide] = useState<'opp' | 'own'>('opp');
  const [defenses5Star, setDefenses5Star] = useState<DefenseData[]>([]);
  const [defenses4Star, setDefenses4Star] = useState<DefenseData[]>([]);

  // 현재 필터에 맞는 방덱 목록
  const defenses = starFilter === 4 ? defenses4Star : defenses5Star;

  // 시즌 목록 가져오기
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const res = await fetch('/api/posts/seasons');
        const data = await res.json();
        if (data.seasons && Array.isArray(data.seasons)) {
          setSeasons(data.seasons);
        }
      } catch (error) {
        console.error('시즌 목록 조회 실패:', error);
      }
    };
    fetchSeasons();
  }, []);

  // 인기 방덱 가져오기 (5성, 4성 둘 다)
  useEffect(() => {
    const fetchPopularDefenses = async () => {
      try {
        setLoading(true);
        setSelectedDefense(null);
        setDefenses5Star([]);
        setDefenses4Star([]);
        const seasonParam = (selectedSeason ? `&season=${selectedSeason}` : '') + (since ? `&since=${encodeURIComponent(since)}` : '');

        // 5성, 4성 동시에 가져오기
        const [res5, res4] = await Promise.all([
          fetch(`/api/posts/popular-defenses?minCount=${minCount}${seasonParam}&stars=5&side=${defenseSide}`),
          fetch(`/api/posts/popular-defenses?minCount=${minCount}${seasonParam}&stars=4&side=${defenseSide}`)
        ]);

        const [data5, data4] = await Promise.all([res5.json(), res4.json()]);

        if (data5.success && Array.isArray(data5.defenses)) {
          setDefenses5Star(data5.defenses);
        }
        if (data4.success && Array.isArray(data4.defenses)) {
          setDefenses4Star(data4.defenses);
        }
      } catch (error) {
        console.error('인기 방덱 조회 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPopularDefenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason, defenseSide, since]);

  const handleSelectDefense = async (defense: DefenseData) => {
    setSelectedDefense(defense);
    setLoadingCounters(true);
    setCounterList(null);

    try {
      const res = await fetch('/api/posts/search-counter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monsters: defense.defenseTeam, side: defenseSide })
      });
      const data = await res.json();
      // 우리 방덱이면 카운터가 아니라 방어 기록 목록이 온다(mode: 'defense-records')
      setCounterList({
        counters: Array.isArray(data.counters) ? data.counters : [],
        records: Array.isArray(data.records) ? data.records : null
      });
    } catch (error) {
      console.error('카운터 조회 실패:', error);
    } finally {
      setLoadingCounters(false);
    }
  };

  // 공덱 그룹 렌더링 (CounterList와 동일한 로직)
  const SIZE = { pad: '10px 14px', avatar: 28, name: 14, badge: 72, badgeFont: 12, date: 76, dateFont: 12 };
  /**
   * 우리 방덱을 골랐을 때 오른쪽에 뜨는 목록.
   *
   * 방어 기록에는 **상대가 어떤 공덱으로 쳤는지가 없다**(게임이 안 준다). 그래서 카운터
   * 목록이 아니라 승패·길드원·방덱만 있는 전투기록 목록을 그린다.
   */
  const renderDefenseRecords = () => {
    const records: any[] = counterList?.records || [];
    if (records.length === 0) {
      return <div style={{ textAlign: 'center', padding: 40, color: '#999', fontSize: 14 }}>방어 기록이 없습니다.</div>;
    }
    return (
      <>
      {/* 날짜별로 얼마나 막았나 — 방덱 탭과 같은 그림이다 */}
      <DefenseTrend records={records} compact={false} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {records.map((r: any, i: number) => (
          <div
            key={r._id || i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: SIZE.pad,
              borderRadius: 12,
              background: i % 2 === 0 ? 'rgba(248, 250, 255, 0.9)' : 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(102, 126, 234, 0.12)'
            }}
          >
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <ProfileWithName
                profileImg={r.profileImg}
                nickname={r.author}
                size={SIZE.avatar}
                fontSize={SIZE.name}
                borderRadius={8}
                color={r.isDeleted ? '#ff6b6b' : '#333'}
              />
            </div>
            {/*
              방덱 그림은 **머리줄에 이미 있다** — 줄마다 같은 세 마리를 또 그리면 자리만
              먹고 상대 길드·날짜가 밀린다. 이 목록은 한 방덱의 기록이라 더 적을 것이 없다.
            */}
            {/* 승패는 방어한 길드원 기준이다 — 막아냈으면 승 */}
            <div style={{
              flexShrink: 0,
              width: SIZE.badge,
              textAlign: 'center',
              fontSize: SIZE.badgeFont,
              fontWeight: 800,
              color: r.result === 'win' ? '#64B5F6' : '#e57373'
            }}>
              {r.result === 'win' ? '방어 성공' : '방어 실패'}
            </div>
            {/*
              **어느 길드가 쳤는지.** 같은 방덱이라도 상대에 따라 결과가 갈린다 — 방덱 탭의
              기록 목록과 같은 자리에 같은 값을 둔다.
            */}
            <div style={{
              flexShrink: 0, width: 110, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5,
              fontSize: SIZE.dateFont, color: '#888', overflow: 'hidden', whiteSpace: 'nowrap'
            }}>
              {r.oppGuildName && (
                <img loading="lazy"
                  src={guildImg(r.oppGuildName)}
                  alt=""
                  style={{ width: 18, height: 18, borderRadius: 5, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(0, 0, 0, 0.08)' }}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/images/unknown2.png'; }}
                />
              )}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.oppGuildName || ''}</span>
            </div>
            <div style={{ flexShrink: 0, width: SIZE.date, textAlign: 'right', fontSize: SIZE.dateFont, color: '#999' }}>{r.date}</div>
          </div>
        ))}
      </div>
      </>
    );
  };

  const renderCounterGroups = () => {
    if (!counterList?.counters || counterList.counters.length === 0) {
      return <div style={{ color: '#999', fontSize: 14, padding: 32, textAlign: 'center' }}>카운터 정보가 없습니다.</div>;
    }

    // 공략과 전투기록 분리
    const tactics = counterList.counters.filter((c: any) => c.source === 'tactic');
    const posts = counterList.counters.filter((c: any) => c.source === 'db');

    // 전투기록만 groupId로 그룹화
    const groupMap = new Map<string, any[]>();
    posts.forEach((counter: any) => {
      const gid = counter.groupId || counter._id;
      if (!groupMap.has(gid)) {
        groupMap.set(gid, []);
      }
      groupMap.get(gid)!.push(counter);
    });

    const groups = Array.from(groupMap.entries()).map(([key, items]) => ({ key, items }));
    groups.sort((a, b) => b.items.length - a.items.length);

    return (
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* 공략 먼저 표시 */}
        {tactics.map((tactic: any, idx: number) => (
          <li
            key={tactic._id || `tactic-${idx}`}
            style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%)',
              borderRadius: 12,
              boxShadow: '0 3px 12px rgba(255, 193, 7, 0.3)',
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: '2px solid rgba(255, 193, 7, 0.5)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 193, 7, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 3px 12px rgba(255, 193, 7, 0.3)';
            }}
            onClick={async () => {
              if (tactic._id) {
                try {
                  const res = await fetch(`/api/tactics/${tactic._id}`, { credentials: 'include' });
                  const data = await res.json();
                  setSelectedPost({ ...data, nickname: tactic.author, profileImg: tactic.profileImg, source: tactic.source });
                } catch {}
              }
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              color: '#fff', fontSize: 10, fontWeight: 800, padding: '4px 8px', borderRadius: 6
            }}>공략</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {(tactic.monsters || tactic.attackTeam)?.map((m: any, mIdx: number) => {
                const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                return imgPath ? (
                  <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', background: '#fff', border: '1.5px solid rgba(102, 126, 234, 0.15)' }} />
                ) : (
                  <div key={mIdx} style={{ width: 36, height: 36, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 8 }}>{displayName}</div>
                );
              })}
            </div>
            <div style={{ flex: 1 }} />
            <img loading="lazy" src={tactic.profileImg || '/images/default-profile.png'} alt="프로필" style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }} />
            <span style={{ color: '#333', fontWeight: 600, fontSize: 14 }}>{tactic.author}</span>
          </li>
        ))}

        {/* 전투기록 그룹 */}
        {groups.map((group) => (
          <React.Fragment key={group.key}>
            {group.items.length > 1 ? (
              <li
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 12,
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.08)',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => { setSelectedGroup(group); setGroupTab('members'); }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.08)';
                }}
              >
                <div style={{ display: 'flex', gap: 4 }}>
                  {(group.items[0].monsters || group.items[0].attackTeam)?.map((m: any, mIdx: number) => {
                    const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                    return imgPath ? (
                      <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', background: '#fff', border: '1.5px solid rgba(102, 126, 234, 0.15)' }} />
                    ) : (
                      <div key={mIdx} style={{ width: 36, height: 36, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 8 }}>{displayName}</div>
                    );
                  })}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                  <div style={{ fontSize: 12, color: '#999', fontWeight: 600 }}>전투 {group.items[0].totalMatches || group.items.length}회</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(0, 0, 0, 0.02)', padding: '5px 14px', borderRadius: 14 }}>
                    <span style={{ fontSize: 12, color: '#64B5F6', fontWeight: 700, minWidth: 32, textAlign: 'center' }}>{group.items[0].wins || 0}승</span>
                    <div style={{ width: 1, height: 12, background: 'rgba(0, 0, 0, 0.1)' }} />
                    <span style={{ fontSize: 12, color: '#e57373', fontWeight: 700, minWidth: 32, textAlign: 'center' }}>{group.items[0].losses || 0}패</span>
                    <div style={{ width: 1, height: 12, background: 'rgba(0, 0, 0, 0.1)' }} />
                    <span style={{ fontSize: 12, color: '#8b7355', fontWeight: 700, minWidth: 50, textAlign: 'center' }}>{(group.items[0].winRate || 0).toFixed(1)}%</span>
                  </div>
                </div>
                <div style={{ fontSize: 14, color: '#667eea', fontWeight: 700 }}><Caret /></div>
              </li>
            ) : (
              group.items.map((c, itemIdx) => {
                const isWin = c.result === 'win';
                const isLose = c.result === 'lose';
                return (
                  <li
                    key={c._id || `${group.key}-${itemIdx}`}
                    style={{
                      background: isLose ? 'rgba(255, 235, 238, 0.9)' : isWin ? 'rgba(227, 242, 253, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                      borderRadius: 12,
                      boxShadow: isLose ? '0 2px 8px rgba(244, 67, 54, 0.12)' : isWin ? '0 2px 8px rgba(33, 150, 243, 0.12)' : '0 2px 8px rgba(102, 126, 234, 0.08)',
                      padding: '14px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      border: isLose ? '1px solid rgba(244, 67, 54, 0.25)' : isWin ? '1px solid rgba(33, 150, 243, 0.25)' : '1px solid rgba(102, 126, 234, 0.1)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.08)';
                    }}
                    onClick={() => {
                      openCounter(c);
                    }}
                  >
                    <div style={{ background: isWin ? 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)' : isLose ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' : '#999', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, minWidth: 30, textAlign: 'center', flexShrink: 0 }}>{isWin ? '승' : isLose ? '패' : '-'}</div>
                    <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 100, flexShrink: 0 }}>
                      <img loading="lazy" src={c.profileImg || '/images/default-profile.png'} alt="프로필" style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'cover', border: '1px solid rgba(102, 126, 234, 0.2)', flexShrink: 0 }} onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }} />
                      <span style={{ fontWeight: 600, fontSize: 12, color: c.isDeleted ? '#ff6b6b' : '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.author}</span>
                    </div>
                    <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <div style={{ display: 'flex', gap: 2 }}>
                        {(c.monsters || c.attackTeam)?.map((m: any, mIdx: number) => {
                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                          return imgPath ? (
                            <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 24, height: 24, borderRadius: 5, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102,126,234,0.15)' }} />
                          ) : (
                            <div key={mIdx} style={{ width: 24, height: 24, borderRadius: 5, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 7 }}>{displayName}</div>
                          );
                        })}
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#667eea', flexShrink: 0 }}>VS</span>
                      <div style={{ display: 'flex', gap: 2 }}>
                        {selectedDefense?.defenseTeam?.map((m: any, mIdx: number) => {
                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                          return imgPath ? (
                            <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 24, height: 24, borderRadius: 5, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102,126,234,0.15)' }} />
                          ) : (
                            <div key={mIdx} style={{ width: 24, height: 24, borderRadius: 5, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 7 }}>{displayName}</div>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#999', flexShrink: 0, marginLeft: 'auto' }}>{c.date}</span>
                  </li>
                );
              })
            )}
          </React.Fragment>
        ))}
      </ul>
    );
  };

  // 뒤로 가기: 글 창 → 그룹 상세 창 → 고른 방덱의 전투기록 판 순으로 닫는다
  useBackHandler(!!selectedDefense, () => setSelectedDefense(null));
  useBackHandler(!!selectedGroup, () => setSelectedGroup(null));
  useBackHandler(!!selectedPost, () => setSelectedPost(null));

  return (
    <div style={{
      padding: 24,
      textAlign: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 20px'
      }}>
        {/* 헤더 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: '40px 32px 32px',
          marginBottom: 32,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          width: '100%',
          position: 'relative'
        }}>
          <h2 style={{
            margin: 0, marginBottom: 8, fontWeight: 900, fontSize: 40, letterSpacing: -1, textAlign: 'center',
            background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
          }}>방덱 통계</h2>
          <p style={{ margin: 0, marginBottom: 20, fontSize: 14, color: '#888', fontWeight: 500, textAlign: 'center' }}>
            전투기록이 {minCount}회 이상인 방덱들만 추려놨습니다
          </p>
          {/*
            거르개는 **묶음별로 붙여** 세 덩어리로 보이게 한다. 알약 여섯 개가 흩어져 있을 때는
            어느 것이 시즌이고 어느 것이 성급인지 눈으로 가릴 수 없었다.
          */}
          {/*
            세 묶음을 **한 줄에** 놓는다 — 공덱·길드원 통계와 같은 차림 (사용자 2026-10-27). 예전에는 세 줄로 쌓았다.
          */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', // 한 줄로 (사용자 2026-10-27 "다른것처럼") — 좁으면 다음 줄로
              gap: 16
            }}>
            <Segmented
              label="시즌"
              value={selectedSeason}
              onChange={setSelectedSeason}
              options={[{ value: null as number | null, label: '전체' },
                        ...seasons.map((s) => ({ value: s as number | null, label: `${s}` }))]}
            />
            <Segmented
              label="길드"
              value={defenseSide}
              onChange={setDefenseSide}
              options={[{ value: 'opp' as const, label: '상대' }, { value: 'own' as const, label: '우리' }]}
            />
            <Segmented
              label="태성"
              value={starFilter}
              onChange={setStarFilter}
              options={[{ value: 5, label: '5성' }, { value: 4, label: '4성' }]}
            />
            </div>
          </div>
          {/*
            뒤로가기는 **판 왼쪽 위 구석에 띄운다.** 제목이 가운데라 그 옆이 비어 있어 자리를
            더 먹지 않는다. 흐름에 두면 단추 하나가 넓은 줄을 통째로 차지했다.
          */}
          {onBack && (
            <div style={{ position: 'absolute', left: 24, top: 24 }}>
              <BackButton onClick={onBack} />
            </div>
          )}
        </div>

        {/* 점령전별 방성률 + 기간 스크롤바 — 공덱·길드원 통계와 같은 조각 */}
        <SiegeRateTrend
          defense={defenseSide}
          season={selectedSeason}
          isMobile={false}
          onPeriodChange={(s, n) => { setSince(s); setRecent(n); }}
        />

        {/* 컨텐츠 */}
        <div style={{ display: 'flex', gap: 24, width: '100%', alignItems: 'flex-start' }}>
          {/* 방덱 목록 */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 24,
            padding: 24,
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            flex: 1,
            minHeight: 400,
            transition: 'all 0.3s ease'
          }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, color: '#667eea', fontSize: 16, fontWeight: 600 }}>
                <div style={{ width: 32, height: 32, border: '4px solid rgba(102, 126, 234, 0.2)', borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: 12 }} />
                불러오는 중...
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : defenses.length === 0 ? (
              <div style={{ color: '#999', fontSize: 16, padding: 48, textAlign: 'center' }}>전투기록이 {minCount}회 이상인 방덱이 없습니다.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {defenses.map((defense, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: selectedDefense === defense ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%)',
                      borderRadius: 16, padding: 16,
                      boxShadow: selectedDefense === defense ? '0 8px 24px rgba(102, 126, 234, 0.4)' : '0 4px 16px rgba(102, 126, 234, 0.1)',
                      border: selectedDefense === defense ? '2px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(102, 126, 234, 0.1)',
                      cursor: 'pointer', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', gap: 16
                    }}
                    onMouseEnter={(e) => { if (selectedDefense !== defense) { e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                    onMouseLeave={(e) => { if (selectedDefense !== defense) { e.currentTarget.style.transform = 'translateY(0)'; } }}
                    onClick={() => handleSelectDefense(defense)}
                  >
                    <div style={{ display: 'flex', gap: 6 }}>
                      {defense.defenseTeam.map((m: any, mIdx: number) => {
                        const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                        return imgPath ? (
                          <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'contain', background: '#fff', border: '2px solid rgba(102, 126, 234, 0.15)' }} />
                        ) : (
                          <div key={mIdx} style={{ width: 48, height: 48, borderRadius: 10, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 9 }}>{displayName}</div>
                        );
                      })}
                    </div>
                    {/*
                      숫자는 **한 줄에 세우고** 빈 가운데에 방성률 막대를 깐다 — 공덱 쪽과 같은
                      짜임이다. 예전에는 왼쪽에 두 줄로 몰려 있고 오른쪽이 텅 비어, 줄끼리
                      견주려면 숫자를 하나씩 읽어야 했다.
                    */}
                    <div style={{
                      fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', width: 150, flexShrink: 0,
                      textAlign: 'left', color: selectedDefense === defense ? '#fff' : '#333'
                    }}>
                      <span style={{ fontWeight: 700 }}>{defense.totalMatches}회</span>
                      <span style={{ color: selectedDefense === defense ? 'rgba(255,255,255,0.9)' : '#64B5F6', marginLeft: 8 }}>{defense.wins}승</span>
                      <span style={{ color: selectedDefense === defense ? 'rgba(255,255,255,0.9)' : '#e57373', marginLeft: 6 }}>{defense.losses}패</span>
                    </div>
                    <div style={{
                      flex: 1, minWidth: 80, height: 8, borderRadius: 999, overflow: 'hidden',
                      background: selectedDefense === defense ? 'rgba(255,255,255,0.25)' : 'rgba(102, 126, 234, 0.12)'
                    }}>
                      <div style={{
                        width: `${Math.max(0, Math.min(100, 100 - defense.winRate))}%`, height: '100%', borderRadius: 999,
                        background: selectedDefense === defense
                          ? 'rgba(255,255,255,0.9)'
                          : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                      }} />
                    </div>
                    <div style={{ width: 70, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: selectedDefense === defense ? '#fff' : '#667eea' }}>
                        {(100 - defense.winRate).toFixed(1)}%
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: selectedDefense === defense ? 'rgba(255,255,255,0.8)' : '#999' }}>방성률</div>
                    </div>
                    <div style={{ fontSize: 16, color: selectedDefense === defense ? '#fff' : '#667eea', fontWeight: 700, flexShrink: 0 }}><Caret /></div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 전투기록 목록 */}
          {selectedDefense && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 24,
              padding: 24,
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
              flex: 1,
              minHeight: 400
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(102, 126, 234, 0.1)' }}>
                <button onClick={() => setSelectedDefense(null)} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', borderRadius: 10, padding: '8px 16px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>← 목록</button>
                <div style={{ display: 'flex', gap: 6 }}>
                  {selectedDefense.defenseTeam.map((m: any, mIdx: number) => {
                    const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                    return imgPath ? <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'contain', background: '#fff', border: '1.5px solid rgba(102, 126, 234, 0.15)' }} /> : null;
                  })}
                </div>
                <div style={{ fontSize: 14, color: '#666', fontWeight: 600 }}>전투기록 목록</div>
              </div>
              {loadingCounters ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, color: '#667eea', fontSize: 14, fontWeight: 600 }}>
                  <div style={{ width: 24, height: 24, border: '3px solid rgba(102, 126, 234, 0.2)', borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: 12 }} />
                  불러오는 중...
                </div>
              ) : (
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>{counterList?.records ? renderDefenseRecords() : renderCounterGroups()}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 그룹 상세 모달 */}
      {selectedGroup && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setSelectedGroup(null)}>
          <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 600, maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {(selectedGroup.items[0].monsters || selectedGroup.items[0].attackTeam)?.map((m: any, idx: number) => {
                  const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                  return imgPath ? <img loading="lazy" key={idx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', background: '#f8faff', border: '2px solid rgba(102, 126, 234, 0.15)' }} /> : null;
                })}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ textAlign: 'left', fontSize: 14, color: '#666', fontWeight: 600, marginBottom: 4 }}>전투 {selectedGroup.items[0].totalMatches || selectedGroup.items.length}회</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <span style={{ fontSize: 14, color: '#64B5F6', fontWeight: 700, minWidth: 40, textAlign: 'center' }}>{selectedGroup.items[0].wins || 0}승</span>
                  <span style={{ fontSize: 14, color: '#e57373', fontWeight: 700, minWidth: 40, textAlign: 'center' }}>{selectedGroup.items[0].losses || 0}패</span>
                  <span style={{ fontSize: 14, color: '#8b7355', fontWeight: 700, minWidth: 50, textAlign: 'center' }}>{(selectedGroup.items[0].winRate || 0).toFixed(1)}%</span>
                </div>
              </div>
              <button onClick={() => setSelectedGroup(null)} style={{ background: 'none', border: 'none', fontSize: 24, color: '#999', cursor: 'pointer', padding: 8 }}>×</button>
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
              <button onClick={() => setGroupTab('members')} data-subtab="" style={{ flex: 1, padding: '14px 0', border: 'none', background: groupTab === 'members' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f5f5f5', color: groupTab === 'members' ? '#fff' : '#666', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>길드원</button>
              <button onClick={() => setGroupTab('records')} data-subtab="" style={{ flex: 1, padding: '14px 0', border: 'none', background: groupTab === 'records' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f5f5f5', color: groupTab === 'records' ? '#fff' : '#666', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>전투기록</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
              {groupTab === 'records' && (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {selectedGroup.items.map((c: any, idx: number) => {
                    const isWin = c.result === 'win';
                    const isLose = c.result === 'lose';
                    return (
                      <li key={c._id || idx} style={{ background: isLose ? 'rgba(255, 235, 238, 0.9)' : isWin ? 'rgba(227, 242, 253, 0.9)' : 'rgba(255, 255, 255, 0.9)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, border: isLose ? '1px solid rgba(244, 67, 54, 0.25)' : isWin ? '1px solid rgba(33, 150, 243, 0.25)' : '1px solid rgba(102, 126, 234, 0.15)', cursor: 'pointer' }}
                        onClick={async () => {
                          openCounter(c);
                              setSelectedGroup(null);
                        }}>
                        <div style={{ background: isWin ? 'linear-gradient(135deg, #64B5F6 0%, #42A5F5 100%)' : isLose ? 'linear-gradient(135deg, #ef9a9a 0%, #e57373 100%)' : '#999', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6 }}>{isWin ? '승' : isLose ? '패' : '-'}</div>
                        <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 100, flexShrink: 0 }}>
                          <img loading="lazy" src={c.profileImg || '/images/default-profile.png'} alt="프로필" style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'cover', border: '1px solid rgba(102, 126, 234, 0.2)', flexShrink: 0 }} onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }} />
                          <span style={{ fontWeight: 600, fontSize: 12, color: c.isDeleted ? '#ff6b6b' : '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.author}</span>
                        </div>
                        <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <div style={{ display: 'flex', gap: 2 }}>
                            {(c.attackTeam || c.monsters)?.map((m: any, mIdx: number) => {
                              const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                              return imgPath ? (
                                <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 24, height: 24, borderRadius: 5, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102,126,234,0.15)' }} />
                              ) : (
                                <div key={mIdx} style={{ width: 24, height: 24, borderRadius: 5, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, color: '#999' }}>{displayName}</div>
                              );
                            })}
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 600, color: '#667eea' }}>VS</span>
                          <div style={{ display: 'flex', gap: 2 }}>
                            {selectedDefense?.defenseTeam?.map((m: any, mIdx: number) => {
                              const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                              return imgPath ? (
                                <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 24, height: 24, borderRadius: 5, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102,126,234,0.15)' }} />
                              ) : (
                                <div key={mIdx} style={{ width: 24, height: 24, borderRadius: 5, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, color: '#999' }}>{displayName}</div>
                              );
                            })}
                          </div>
                        </div>
                        <div style={{ flex: 1 }} />
                        {c.date && <span style={{ fontSize: 12, color: '#999' }}>{c.date}</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
              {groupTab === 'members' && (() => {
                const memberStats = new Map<string, { nickname: string; profileImg: string; wins: number; losses: number; records: any[]; key: string }>();
                selectedGroup.items.forEach((c: any) => {
                  const key = c.authorUuid || c.author;
                  if (!memberStats.has(key)) memberStats.set(key, { nickname: c.author, profileImg: c.profileImg, wins: 0, losses: 0, records: [], key });
                  const stat = memberStats.get(key)!;
                  if (c.result === 'win') stat.wins++;
                  else if (c.result === 'lose') stat.losses++;
                  stat.records.push(c);
                });
                const sortedMembers = Array.from(memberStats.values()).sort((a, b) => {
                  const aRate = a.wins + a.losses > 0 ? a.wins / (a.wins + a.losses) : 0;
                  const bRate = b.wins + b.losses > 0 ? b.wins / (b.wins + b.losses) : 0;
                  if (bRate !== aRate) return bRate - aRate;
                  return (b.wins + b.losses) - (a.wins + a.losses);
                });
                return (
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {sortedMembers.map((member, idx) => {
                      const total = member.wins + member.losses;
                      const winRate = total > 0 ? ((member.wins / total) * 100).toFixed(1) : '0.0';
                      const isExpanded = expandedMemberKey === member.key;
                      return (
                        <li key={idx} style={{ background: 'rgba(255, 255, 255, 0.9)', borderRadius: 12, border: isExpanded ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(102, 126, 234, 0.15)', overflow: 'hidden' }}>
                          <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }} onClick={() => setExpandedMemberKey(isExpanded ? null : member.key)}>
                            <img loading="lazy" src={member.profileImg || '/images/default-profile.png'} alt="프로필" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', border: '2px solid rgba(102, 126, 234, 0.2)' }} onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 15, color: '#333', marginBottom: 4 }}>{member.nickname}</div>
                              <div style={{ fontSize: 12, color: '#888' }}>전투 {total}회</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(0, 0, 0, 0.03)', padding: '8px 14px', borderRadius: 10 }}>
                              <span style={{ fontSize: 14, color: '#64B5F6', fontWeight: 700, minWidth: 40, textAlign: 'center' }}>{member.wins}승</span>
                              <span style={{ fontSize: 14, color: '#e57373', fontWeight: 700, minWidth: 40, textAlign: 'center' }}>{member.losses}패</span>
                              <span style={{ fontSize: 14, color: '#8b7355', fontWeight: 700, minWidth: 50, textAlign: 'center' }}>{winRate}%</span>
                            </div>
                            <div style={{ fontSize: 14, color: '#667eea', fontWeight: 700, transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}><Caret /></div>
                          </div>
                          {isExpanded && (
                            <div style={{ borderTop: '1px solid rgba(102, 126, 234, 0.1)', background: 'rgba(102, 126, 234, 0.02)', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {member.records.map((c: any, rIdx: number) => {
                                const isWin = c.result === 'win';
                                const isLose = c.result === 'lose';
                                return (
                                  <div key={c._id || rIdx} style={{ background: isLose ? 'rgba(255, 235, 238, 0.9)' : isWin ? 'rgba(227, 242, 253, 0.9)' : 'rgba(255, 255, 255, 0.9)', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      openCounter(c);
                              setSelectedGroup(null);
                                    }}>
                                    <div style={{ background: isWin ? 'linear-gradient(135deg, #64B5F6 0%, #42A5F5 100%)' : isLose ? 'linear-gradient(135deg, #ef9a9a 0%, #e57373 100%)' : '#999', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6 }}>{isWin ? '승' : isLose ? '패' : '-'}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                      <div style={{ display: 'flex', gap: 2 }}>
                                        {(c.attackTeam || c.monsters)?.map((m: any, mIdx: number) => {
                                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                          return imgPath ? (
                                            <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102,126,234,0.15)' }} />
                                          ) : (
                                            <div key={mIdx} style={{ width: 22, height: 22, borderRadius: 5, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, color: '#999' }}>{displayName}</div>
                                          );
                                        })}
                                      </div>
                                      <span style={{ fontSize: 10, fontWeight: 600, color: '#667eea' }}>VS</span>
                                      <div style={{ display: 'flex', gap: 2 }}>
                                        {selectedDefense?.defenseTeam?.map((m: any, mIdx: number) => {
                                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                          return imgPath ? (
                                            <img loading="lazy" key={mIdx} src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102,126,234,0.15)' }} />
                                          ) : (
                                            <div key={mIdx} style={{ width: 22, height: 22, borderRadius: 5, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, color: '#999' }}>{displayName}</div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    <div style={{ flex: 1 }} />
                                    <div style={{ fontSize: 12, color: '#999' }}>{c.date}</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* 게시글 팝업 */}
      {selectedPost?.source === 'tactic' ? (
        <TacticPost open={!!selectedPost} onClose={() => setSelectedPost(null)} tactic={selectedPost} monsterMapping={monsterMapping} user={user} />
      ) : (
        <CounterPost open={!!selectedPost} onClose={() => setSelectedPost(null)} post={selectedPost} monsterMapping={monsterMapping} user={user} />
      )}
    </div>
  );
};

export default PopularDefense;
